// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Teeter — the PURE tilt derivation (principle #23): raw device orientation angles →
// a gravity vector {x,y} in [-1,1]^2. This is the single function every unit test
// drives, so the sensor path can be proven without a real gyroscope. The live gyro
// source (input.ts) does nothing but read DeviceOrientationEvent angles and hand them
// here; the physics never sees an angle. No sample is recorded, stored, or sent.
//
//   gamma: left/right tilt, -90..90  ->  x (right positive)
//   beta:  front/back tilt, -180..180 ->  y (toward you / "down the screen" positive)
//
// A per-round NEUTRAL calibration means "however you are holding it now" becomes
// level, so playing lying down or on a desk is comfortable. Screen orientation is
// applied by rotating the resulting vector, so landscape play is correct too.

export interface Orientation {
  /** Front/back tilt in degrees (DeviceOrientationEvent.beta). */
  beta: number;
  /** Left/right tilt in degrees (DeviceOrientationEvent.gamma). */
  gamma: number;
}

export interface TiltConfig {
  /** Degrees of tilt that map to full deflection (1.0). Default 28. */
  range?: number;
  /** Dead zone as a fraction of full deflection, killed and rescaled. Default 0.06. */
  deadZone?: number;
  /** Screen rotation in degrees (screen.orientation.angle): 0, 90, 180, 270. */
  screenAngle?: number;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Kill a small dead zone and rescale so output ramps from 0 at the edge of it. */
function applyDeadZone(v: number, dz: number): number {
  const a = Math.abs(v);
  if (a <= dz) return 0;
  return Math.sign(v) * ((a - dz) / (1 - dz));
}

/**
 * Map raw orientation to a gravity vector in [-1,1]^2, relative to a neutral pose.
 * Pure and deterministic — the whole sensor contract lives here.
 */
export function tiltFromOrientation(o: Orientation, neutral: Orientation, cfg: TiltConfig = {}): {
  x: number;
  y: number;
} {
  const range = cfg.range ?? 28;
  const dz = cfg.deadZone ?? 0.06;
  const angle = ((cfg.screenAngle ?? 0) % 360 + 360) % 360;

  let x = clamp((o.gamma - neutral.gamma) / range, -1, 1);
  let y = clamp((o.beta - neutral.beta) / range, -1, 1);
  x = applyDeadZone(x, dz);
  y = applyDeadZone(y, dz);

  // Rotate the intent by the screen orientation so "tilt right" is always screen-right.
  switch (angle) {
    case 90:
      return { x: -y, y: x };
    case 180:
      return { x: -x, y: -y };
    case 270:
      return { x: y, y: -x };
    default:
      return { x, y };
  }
}

/** Clamp an arbitrary steering vector (joystick/keys) to the unit disc. */
export function clampVector(x: number, y: number): { x: number; y: number } {
  const mag = Math.hypot(x, y);
  if (mag <= 1) return { x, y };
  return { x: x / mag, y: y / mag };
}
